import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  TrendingUp as TrendingUpIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { reportService } from '../../../services/reportServices';
import { formatCurrency } from '../../../utils/formatNumber';
import dayjs from 'dayjs';

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const grades = ['1A', '1B', '2', '3', '4', '5', '6&7', '8&9', '10', 'Prescolar'];

const MissingPaymentReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedColumn, setCopiedColumn] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedCount, setCopiedCount] = useState(0);

    const handleCopyColumn = (columnName: string, data: any[]) => {
        // Extraer los valores de la columna según el nombre
        let values: string[] = [];
        
        switch (columnName) {
            case 'full_name':
            values = data.map(s => s.full_name);
            break;
            case 'grade':
            values = data.map(s => s.grade);
            break;
            case 'sponsor':
            values = data.map(s => s.sponsor || '-');
            break;
            case 'amount_due':
            values = data.map(s => formatCurrency(s.amount_due));
            break;
            case 'status':
            values = data.map(s => 
                s.status === 'partial' ? 'Parcial' :
                s.status === 'paid' ? 'Pagado' : 'Pendiente'
            );
            break;
            case 'parent_name':
            values = data.map(s => s.parent_name || '-');
            break;
            default:
            return;
        }
        
        const textToCopy = values.join('\n');
        
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopiedColumn(columnName);
            setCopiedCount(values.length);
            setSnackbarOpen(true);
        });
    };

    // Mapeo de nombres de columnas para mostrar
    const columnLabels: Record<string, string> = {
        full_name: 'Estudiante',
        grade: 'Grado',
        sponsor: 'Patrocinador',
        amount_due: 'Monto a Pagar',
        status: 'Estado',
        parent_name: 'Padre/Tutor'
    };

  
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      month: dayjs().format('MMMM'),
      year: dayjs().year(),
      grade: ''
    }
  });

  const watchMonth = watch('month');
  const watchYear = watch('year');

  const loadReport = async (data: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getStudentsMissingPayment({
        month: data.month,
        year: data.year,
        grade: data.grade || undefined
      });
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

    const handleExportExcel = async () => {
        try {
        const params = new URLSearchParams({
            month: watchMonth,
            year: watchYear.toString(),
            grade: watch('grade') || ''
        });
        const response = await reportService.exportMissingPaymentExcel(params);
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `estudiantes_sin_pagar_${watchMonth}_${watchYear}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        } catch (err) {
        setError('Error al exportar a Excel');
        }
    };

    const handleExportPDF = async () => {
        try {
        const params = new URLSearchParams({
            month: watchMonth,
            year: watchYear.toString(),
            grade: watch('grade') || ''
        });
        const response = await reportService.exportMissingPaymentPDF(params);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `estudiantes_sin_pagar_${watchMonth}_${watchYear}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        } catch (err) {
        setError('Error al exportar a PDF');
        }
    };

    const getStatusChip = (status: string) => {
        if (status === 'partial') {
        return <Chip label="Parcial" color="info" size="small" />;
        }
        if (status === 'paid') {
        return <Chip label="Pagado" color="success" size="small" />;
        }
        return <Chip label="Pendiente" color="warning" size="small" />;
    };

    /*const handleCellClick = (value: any, label: string) => {
    const textToCopy = value?.toString() || '';
    if (textToCopy) {
        navigator.clipboard.writeText(textToCopy);
        setCopiedValue(`${label}: ${textToCopy}`);
        setSnackbarOpen(true);
    }
    };*/

    const cellStyles = {
        cursor: 'pointer',
        '&:hover': {
            bgcolor: 'action.hover',
        },
        '&:hover .copy-icon': {
            opacity: 1,
        },
    };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Estudiantes sin Pago por Mes
        </Typography>

        <form onSubmit={handleSubmit(loadReport)}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Controller
                name="month"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Mes *</InputLabel>
                    <Select {...field} label="Mes *">
                      {months.map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Controller
                name="year"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Año *"
                    size="small"
                    inputProps={{ min: 2020, max: 2030 }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Controller
                name="grade"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Grado</InputLabel>
                    <Select {...field} label="Grado">
                      <MenuItem value="">Todos</MenuItem>
                      {grades.map((g) => (
                        <MenuItem key={g} value={g}>{g}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <Box display="flex" gap={1}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generar'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {reportData && (
          <>
            {/* Tarjetas de resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Estudiantes Sin Pago
                  </Typography>
                  <Typography variant="h5">
                    {reportData.summary.total_students}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Monto Pendiente
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {formatCurrency(reportData.summary.total_pending_amount)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Pagos Parciales
                  </Typography>
                  <Typography variant="h5">
                    {reportData.summary.students_with_partial}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Promedio por Estudiante
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(reportData.summary.total_students > 0 
                      ? reportData.summary.total_pending_amount / reportData.summary.total_students 
                      : 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Botones de exportación */}
            <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExcelIcon />}
                onClick={handleExportExcel}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
              >
                Exportar PDF
              </Button>
            </Box>

            {/* Tabla */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('full_name', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" gap={1}>
                            Estudiante
                            <Tooltip title="Copiar columna completa">
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Tooltip>
                        </Box>
                        </TableCell>
                        <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('grade', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" gap={1}>
                            Grado
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                        </TableCell>
                        <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('sponsor', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" gap={1}>
                            Patrocinador
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                        </TableCell>
                        <TableCell 
                        align="right"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('amount_due', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            Monto a Pagar
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                        </TableCell>
                        <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('status', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" gap={1}>
                            Estado
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                        </TableCell>
                        <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                        onClick={() => handleCopyColumn('parent_name', reportData.students)}
                        >
                        <Box display="flex" alignItems="center" gap={1}>
                            Padre/Tutor
                            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                        </TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {reportData.students.map((student: any, index: number) => (
                        <TableRow key={student.id}>
                        <TableCell>{student.full_name}</TableCell>
                        <TableCell>{student.grade}</TableCell>
                        <TableCell>{student.sponsor || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(student.amount_due)}</TableCell>
                        <TableCell>
                            {getStatusChip(student.status)}
                        </TableCell>
                        <TableCell>
                            <Tooltip title={student.parent_phone || ''}>
                            <Typography variant="caption" display="block">
                                {student.parent_name || '-'}
                            </Typography>
                            </Tooltip>
                        </TableCell>
                        </TableRow>
                    ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Snackbar de confirmación */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    severity="success" 
                    onClose={() => setSnackbarOpen(false)}
                    icon={<CheckIcon />}
                >
                    {copiedCount} valores de la columna "{columnLabels[copiedColumn || '']}" copiados
                </Alert>
            </Snackbar>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MissingPaymentReport;